import { useState } from 'react'
import { Sparkles, Loader2, X, FileText, Check } from 'lucide-react'
import { rfpApi } from '@/api/client'
import type { DealTranscript, HubspotExcerpt } from '@/api/client'
import { showToast } from '@/store/uiStore'
import { TranscriptSourcePanel } from '@features/scope/components/TranscriptSourcePanel'
import type { QualificationChecklist, QualYesNo, PresalesRecommendation } from '../types'
import { RFP_PRODUCTS } from '../types'

interface AiSuggestion {
  value: string | string[] | null
  confidence: 'high' | 'medium' | 'low'
  source: string
}

interface AiAssistResult {
  suggestions: Record<string, AiSuggestion>
}

const FIELD_LABELS: Record<string, { label: string; type: 'checklist' | 'date' | 'products' | 'yesno' | 'recommendation' | 'project' }> = {
  company_overview: { label: 'Company Overview', type: 'project' },
  regulatory_environment: { label: 'Regulatory Environment', type: 'project' },
  industry_context: { label: 'Industry Context', type: 'project' },
  products: { label: 'Products', type: 'products' },
  high_volume: { label: 'High Volume?', type: 'yesno' },
  multi_product: { label: 'Multi-Product?', type: 'yesno' },
  multi_country: { label: 'Multi-Country?', type: 'yesno' },
  target_segment: { label: 'Target Segment?', type: 'yesno' },
  competitors: { label: 'Competitors', type: 'checklist' },
  current_vendor: { label: 'Current Vendor', type: 'checklist' },
  current_solution: { label: 'Current Solution', type: 'checklist' },
  pain_points: { label: 'Pain Points / Gaps', type: 'checklist' },
  reason_for_change: { label: 'Reason for Change', type: 'checklist' },
  question_cutoff_date: { label: 'Question Cutoff Date', type: 'date' },
  response_due_date: { label: 'Response Due Date', type: 'date' },
  presales_recommendation: { label: 'Pre-sales Recommendation', type: 'recommendation' },
}

const CONFIDENCE_CONFIG = {
  high: { label: 'High', bg: 'bg-emerald-500/20', color: 'text-emerald-400' },
  medium: { label: 'Medium', bg: 'bg-amber-500/20', color: 'text-amber-400' },
  low: { label: 'Low', bg: 'bg-red-500/20', color: 'text-red-400' },
}

interface RfpAiAssistProps {
  projectId: number
  dealId: number | null
  checklist: QualificationChecklist
  questionCutoffDate: string | null
  responseDueDate: string | null
  companyOverview: string | null
  regulatoryEnvironment: string | null
  industryContext: string | null
  requirementCount?: number
  onApplyChecklist: (cl: QualificationChecklist) => void
  onApplyDate: (field: 'question_cutoff_date' | 'response_due_date', value: string | null) => void
  onApplyProject: (field: string, value: string) => void
}

export function RfpAiAssist({ projectId, dealId, checklist, questionCutoffDate, responseDueDate, companyOverview, regulatoryEnvironment, industryContext, requirementCount, onApplyChecklist, onApplyDate, onApplyProject }: RfpAiAssistProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notes, setNotes] = useState('')
  const [result, setResult] = useState<AiAssistResult | null>(null)
  const [applied, setApplied] = useState<Set<string>>(new Set())

  // TranscriptSourcePanel state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [includeExcerpt, setIncludeExcerpt] = useState(true)
  const [hubspotExcerpt, setHubspotExcerpt] = useState<HubspotExcerpt | null>(null)
  const [excerptLoading, setExcerptLoading] = useState(false)

  const toggleTranscript = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleTranscriptsLoaded = (transcripts: DealTranscript[]) => {
    setSelectedIds(new Set(transcripts.map(t => t.id)))
  }

  const hasInput = notes.trim() || selectedIds.size > 0 || hubspotExcerpt || dealId || (requirementCount && requirementCount > 0)

  const handleGenerate = async () => {
    setLoading(true)
    setResult(null)
    setApplied(new Set())
    try {
      const res = await rfpApi.aiAssist(projectId, {
        notes: notes.trim() || undefined,
        transcript_ids: Array.from(selectedIds),
      })
      const data = (res?.data as any)?.data ?? res?.data
      if (data?.suggestions) {
        setResult(data)
      } else {
        const errMsg = (res as any)?.error || (res?.data as any)?.error || 'Failed to generate suggestions'
        showToast(errMsg, 'error')
      }
    } catch (err: any) {
      showToast(err?.message || 'Failed to generate suggestions', 'error')
    }
    setLoading(false)
  }

  const applyFields = (fields: Array<[string, AiSuggestion]>) => {
    let mergedChecklist = { ...checklist }
    let checklistChanged = false
    const appliedFields: string[] = []

    for (const [field, suggestion] of fields) {
      const fieldInfo = FIELD_LABELS[field]
      if (!fieldInfo || suggestion.value == null) continue

      if (fieldInfo.type === 'project') {
        onApplyProject(field, suggestion.value as string)
      } else if (fieldInfo.type === 'date') {
        onApplyDate(field as 'question_cutoff_date' | 'response_due_date', suggestion.value as string)
      } else if (fieldInfo.type === 'products') {
        mergedChecklist = { ...mergedChecklist, products: suggestion.value as string[] }
        checklistChanged = true
      } else if (fieldInfo.type === 'yesno') {
        mergedChecklist = { ...mergedChecklist, [field]: suggestion.value as QualYesNo }
        checklistChanged = true
      } else if (fieldInfo.type === 'recommendation') {
        mergedChecklist = { ...mergedChecklist, presales_recommendation: suggestion.value as PresalesRecommendation }
        checklistChanged = true
      } else {
        mergedChecklist = { ...mergedChecklist, [field]: suggestion.value as string }
        checklistChanged = true
      }
      appliedFields.push(field)
    }

    if (checklistChanged) onApplyChecklist(mergedChecklist)
    setApplied(prev => new Set([...prev, ...appliedFields]))
  }

  const handleApply = (field: string, suggestion: AiSuggestion) => {
    applyFields([[field, suggestion]])
  }

  const getCurrentValue = (field: string): string => {
    if (field === 'company_overview') return companyOverview || ''
    if (field === 'regulatory_environment') return regulatoryEnvironment || ''
    if (field === 'industry_context') return industryContext || ''
    if (field === 'question_cutoff_date') return questionCutoffDate || ''
    if (field === 'response_due_date') return responseDueDate || ''
    if (field === 'products') {
      const prods = checklist.products || []
      return prods.map(k => RFP_PRODUCTS.find(p => p.key === k)?.label || k).join(', ')
    }
    const val = (checklist as unknown as Record<string, unknown>)[field]
    if (val === null || val === undefined) return ''
    return String(val)
  }

  const suggestedFields = result
    ? Object.entries(result.suggestions).filter(([, s]) => s.value != null && (!Array.isArray(s.value) || s.value.length > 0))
    : []

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded-lg hover:bg-violet-500/20 transition-colors">
        <Sparkles size={14} />
        AI Assist
      </button>

      {open && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-[var(--bg-secondary)] rounded-lg shadow-modal" role="dialog" aria-modal="true">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/20 bg-violet-500/5 rounded-t-lg">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-400">AI-Assisted RFP Qualification</span>
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-500/20 text-violet-300 rounded">SUGGESTIONS ONLY</span>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
                <p className="text-xs text-[var(--text-secondary)]">
                  The AI analyses the <strong>imported RFP document</strong>, transcripts, HubSpot excerpts, and any additional notes you provide to <strong>suggest</strong> values for competitors, AS-IS state, dates, and other qualification fields. You review and accept each suggestion individually.
                </p>

                {/* RFP Document */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                    <FileText className="w-3 h-3" /> RFP Document
                  </p>
                  {requirementCount && requirementCount > 0 ? (
                    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
                      <Check size={12} className="text-emerald-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-[var(--text-primary)]">{requirementCount} imported requirements</span>
                      </div>
                      <span className="text-[10px] text-emerald-400 font-medium shrink-0">Auto-included</span>
                    </div>
                  ) : (
                    <p className="text-[11px] text-amber-400 italic py-1">No RFP requirements imported yet — import a document to include it in the analysis.</p>
                  )}
                </div>

                {/* Transcripts & HubSpot Excerpt */}
                {!dealId ? (
                  <p className="text-[11px] text-amber-400 italic py-1">No deal linked — link this RFP to a deal to access transcripts and HubSpot excerpts.</p>
                ) : (
                  <TranscriptSourcePanel
                    dealId={dealId}
                    isActive={open}
                    selectedTranscriptIds={selectedIds}
                    onToggleTranscript={toggleTranscript}
                    onTranscriptsLoaded={handleTranscriptsLoaded}
                    includeExcerpt={includeExcerpt}
                    onToggleExcerpt={setIncludeExcerpt}
                    hubspotExcerpt={hubspotExcerpt}
                    onExcerptChange={setHubspotExcerpt}
                    excerptLoading={excerptLoading}
                    onExcerptLoadingChange={setExcerptLoading}
                  />
                )}

                {/* Additional notes */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Additional Notes (optional)</p>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Paste extra context — email threads, Slack messages, AE intel, or anything not in a saved transcript..."
                    className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-end">
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !hasInput}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing...</> : <><Sparkles className="w-3.5 h-3.5" /> {result ? 'Regenerate' : 'Generate suggestions'}</>}
                  </button>
                </div>

                {/* Results */}
                {result && (
                  <div className="space-y-3 pt-3 border-t border-violet-500/20">
                    {suggestedFields.length === 0 ? (
                      <p className="text-xs text-[var(--text-muted)] italic">No suggestions could be extracted from the provided material.</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                            {suggestedFields.length} suggestion{suggestedFields.length !== 1 ? 's' : ''}
                          </span>
                          {suggestedFields.length > 1 && applied.size < suggestedFields.length && (
                            <button
                              onClick={() => {
                                const unapplied = suggestedFields.filter(([field, s]) => !applied.has(field) && s.value != null)
                                applyFields(unapplied)
                              }}
                              className="text-[10px] font-medium text-violet-400 hover:text-violet-300 transition-colors"
                            >
                              Accept all
                            </button>
                          )}
                        </div>

                        {suggestedFields.map(([field, suggestion]) => {
                          const fieldInfo = FIELD_LABELS[field]
                          if (!fieldInfo || !suggestion.value) return null
                          const confCfg = CONFIDENCE_CONFIG[suggestion.confidence] || CONFIDENCE_CONFIG.low
                          const isApplied = applied.has(field)
                          const currentVal = getCurrentValue(field)

                          return (
                            <div
                              key={field}
                              className={`rounded-lg border p-3 ${
                                isApplied
                                  ? 'border-emerald-500/30 bg-emerald-500/5'
                                  : 'border-[var(--border-color)] bg-[var(--bg-primary)]'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{fieldInfo.label}</span>
                                  <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${confCfg.bg} ${confCfg.color}`}>
                                    {confCfg.label}
                                  </span>
                                </div>
                                {isApplied ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                                ) : (
                                  <button
                                    onClick={() => handleApply(field, suggestion)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors"
                                  >
                                    Use this suggestion
                                  </button>
                                )}
                              </div>
                              {fieldInfo.type === 'products' && Array.isArray(suggestion.value) ? (
                                <div className="flex flex-wrap gap-1">
                                  {(suggestion.value as string[]).map(key => {
                                    const p = RFP_PRODUCTS.find(rp => rp.key === key)
                                    return p ? (
                                      <span key={key} className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-500/10 text-indigo-400">{p.label}</span>
                                    ) : null
                                  })}
                                </div>
                              ) : fieldInfo.type === 'yesno' || fieldInfo.type === 'recommendation' ? (
                                <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded ${
                                  suggestion.value === 'yes' ? 'bg-green-500/10 text-green-400'
                                    : suggestion.value === 'no' ? 'bg-red-500/10 text-red-400'
                                    : 'bg-amber-500/10 text-amber-400'
                                }`}>
                                  {String(suggestion.value).charAt(0).toUpperCase() + String(suggestion.value).slice(1)}
                                </span>
                              ) : (
                                <p className="text-xs text-[var(--text-primary)] whitespace-pre-wrap">{String(suggestion.value)}</p>
                              )}
                              <p className="text-[10px] text-[var(--text-muted)] mt-1 italic">Source: {suggestion.source}</p>
                              {currentVal && !isApplied && (
                                <div className="mt-2 pt-2 border-t border-[var(--border-color)]">
                                  <p className="text-[10px] text-[var(--text-muted)]">Current value:</p>
                                  <p className="text-[11px] text-[var(--text-secondary)]">{currentVal}</p>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
